import { FC, useMemo } from 'react'
import Select, { SingleValue } from 'react-select'
import { useTranslation } from 'react-i18next'
import { useAppTheme } from '../../../theme/useAppTheme'
import { ApiShippingAddress } from '../../../api/quotes/types/ApiShippingAddress'

type AddressOption = { value: string; label: string }

interface CreateQuoteAddressSelectProps {
  addresses: ApiShippingAddress[]
  selectedAddressId: string
  onChange: (addressId: string) => void
}

export const CreateQuoteAddressSelect: FC<CreateQuoteAddressSelectProps> = ({
  addresses,
  selectedAddressId,
  onChange,
}) => {
  const { t } = useTranslation()
  const theme = useAppTheme()

  const options: AddressOption[] = useMemo(
    () => addresses.map((a) => ({ value: a.id, label: a.address })),
    [addresses],
  )

  const selectedOption = options.find((o) => o.value === selectedAddressId) ?? null

  const handleChange = (option: SingleValue<AddressOption>) => {
    if (option) {
      onChange(option.value)
    }
  }

  return (
    <Select<AddressOption>
      value={selectedOption}
      onChange={handleChange}
      options={options}
      isSearchable
      placeholder={t('Select an address')}
      noOptionsMessage={() => t('No addresses found')}
      styles={{
        control: (base) => ({
          ...base,
          borderColor: theme.colors.border.default,
          borderRadius: 4,
          minHeight: 38,
          '&:hover': {
            borderColor: theme.colors.primary.main,
          },
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected
            ? theme.colors.primary.main
            : state.isFocused
              ? theme.colors.background.muted
              : undefined,
          color: state.isSelected ? theme.colors.primary.contrastText : theme.colors.text.primary,
          fontSize: theme.fontSize.regular,
        }),
        singleValue: (base) => ({
          ...base,
          color: theme.colors.text.primary,
          fontSize: theme.fontSize.regular,
        }),
        placeholder: (base) => ({
          ...base,
          color: theme.colors.text.muted,
          fontSize: theme.fontSize.regular,
        }),
        menu: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  )
}
